/* eslint-disable react-hooks/exhaustive-deps */
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Chip, Tooltip } from '@mui/material';
import { Iconify } from 'src/components';
import { LEAD_REFERRAL_TYPES } from 'src/constants/LeadSourceConst';
import { PATH_DASHBOARD } from 'src/routes/paths';

// Map referral types to icons and colors from LeadSourceConst
const REFERRAL_CONFIG = {
    BUS: { icon: 'material-symbols:directions-bus', color: '#F59E0B', bg: '#FEF3C7' },
    CINEMA: { icon: 'maki:cinema', color: '#8B5CF6', bg: '#EDE9FE' },
    EMAIL: { icon: 'ic:email', color: '#3B82F6', bg: '#DBEAFE' },
    EVENTS: { icon: 'mdi:events', color: '#EC4899', bg: '#FCE7F3' },
    EXISTING_PATIENT: { icon: 'material-symbols:recent-patient-outline-rounded', color: '#10B981', bg: '#D1FAE5' },
    FACEBOOK: { icon: 'ri:facebook-fill', color: '#1877F2', bg: '#E7F3FF' },
    INSTAGRAM: { icon: 'mdi:instagram', color: '#E4405F', bg: '#FFE8ED' },
    ONLINE_CHAT: { icon: 'mdi:chat-processing-outline', color: '#06B6D4', bg: '#CFFAFE' },
    ORGANIC_SEARCH: { icon: 'mdi:organic-outline', color: '#10B981', bg: '#D1FAE5' },
    AI_ASSISTANT: { icon: 'mdi:robot-outline', color: '#7C3AED', bg: '#EDE9FE' },
    OTHER_ONLINE_ADVERTISING: { icon: 'ic:baseline-miscellaneous-services', color: '#64748B', bg: '#F1F5F9' },
    PAID_SEARCH: { icon: 'eva:search-outline', color: '#F59E0B', bg: '#FEF3C7' },
    PAID_SOCIAL: { icon: 'material-symbols:share-sharp', color: '#3B82F6', bg: '#DBEAFE' },
    PRINT_ADVERTISING: { icon: 'material-symbols:print-outline', color: '#64748B', bg: '#F1F5F9' },
    RADIO: { icon: 'mingcute:radio-fill', color: '#EF4444', bg: '#FEE2E2' },
    REFERRED_BY_FRIEND: { icon: 'fa-solid:user-friends', color: '#F59E0B', bg: '#FEF3C7' },
    REFERRED_BY_ANOTHER_PRACTICE: { icon: 'eva:home-fill', color: '#8B5CF6', bg: '#EDE9FE' },
    REFERRED_BY_ANOTHER_WEBSITE: { icon: 'mingcute:earth-fill', color: '#3B82F6', bg: '#DBEAFE' },
    SOCIAL: { icon: 'material-symbols:share-sharp', color: '#3B82F6', bg: '#DBEAFE' },
    TIKTOK: { icon: 'ic:twotone-tiktok', color: '#000000', bg: '#F1F5F9' },
    UNAVAILABLE: { icon: 'typcn:cancel-outline', color: '#64748B', bg: '#F1F5F9' },
    VISITED_PRACTICE: { icon: 'medical-icon:i-family-practice', color: '#10B981', bg: '#D1FAE5' },
    WHATSAPP: { icon: 'ri:whatsapp-fill', color: '#25D366', bg: '#E8F8ED' },
    YOUTUBE: { icon: 'ri:youtube-fill', color: '#FF0000', bg: '#FEE2E2' },
    CALENDLY: { icon: 'mi:calendar', color: '#006BFF', bg: '#DBEAFE' },
    SOE: { icon: 'carbon:network-enterprise', color: '#64748B', bg: '#F1F5F9' },
    DENTALLY: { icon: 'akar-icons:dental', color: '#3B82F6', bg: '#DBEAFE' },
    R4: { icon: 'ic:round-4k', color: '#64748B', bg: '#F1F5F9' },
    PRACTICE_SOFTWARE: { icon: 'grommet-icons:cloud-software', color: '#8B5CF6', bg: '#EDE9FE' },
    ONLINE_BOOKING: { icon: 'mdi:invoice', color: '#8B5CF6', bg: '#EDE9FE' },
    GOOGLE: { icon: 'mdi:google', color: '#4285F4', bg: '#E8F0FE' },
    WEBSITE: { icon: 'mdi:web', color: '#6366F1', bg: '#EEF2FF' },
};

const getReferralConfig = (referralName) => {
    if (!referralName) return { icon: 'mdi:account-group', color: '#F59E0B', bg: '#FEF3C7' };

    const name = referralName.toUpperCase().replace(/\s+/g, '_');

    // Try exact match first
    if (REFERRAL_CONFIG[name]) return REFERRAL_CONFIG[name];

    // Try to find from LEAD_REFERRAL_TYPES
    const referralMatch = LEAD_REFERRAL_TYPES.find(
        (r) => r.value === name || r.label.toUpperCase() === name.replace(/_/g, ' ')
    );
    if (referralMatch) {
        return {
            icon: referralMatch.icon,
            color: '#F59E0B',
            bg: '#FEF3C7',
        };
    }

    // Default fallback
    return { icon: 'mdi:account-group', color: '#F59E0B', bg: '#FEF3C7' };
};

const formatCurrency = (value) => {
    if (value >= 1000000) return `£${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `£${(value / 1000).toFixed(1)}K`;
    return `£${value?.toLocaleString() || 0}`;
};

const formatReferralLabel = (name) => {
    if (!name) return 'Unknown';
    // Check LEAD_REFERRAL_TYPES for proper label
    const normalizedName = name.toUpperCase().replace(/\s+/g, '_');
    const referralMatch = LEAD_REFERRAL_TYPES.find(
        (r) => r.value === normalizedName || r.label.toUpperCase() === name.toUpperCase()
    );
    if (referralMatch) return referralMatch.label;
    // Fallback to title case
    return name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

const ReferralBreakdown = ({ referrals, title, total, onReferralClick, showViewAll }) => {
    const navigate = useNavigate();
    const maxLeads = Math.max(...referrals.map((r) => r.leads), 1);

    const handleViewAll = () => {
        navigate(PATH_DASHBOARD.analyticsReports.bySource);
    };

    return (
        <Box
            sx={{
                bgcolor: '#fff',
                borderRadius: '16px',
                p: '24px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 3,
                }}
            >
                <Tooltip title="Specific referral sources driving new patient enquiries. Only leads with a referral recorded are counted, so this total is lower than the overall lead count." placement="bottom" arrow
                    slotProps={{ tooltip: { sx: { bgcolor: '#1e293b', fontSize: '12px', maxWidth: 280 } } }}>
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, cursor: 'help' }}>
                        <Iconify icon="mdi:account-group" width={20} height={20} sx={{ color: '#f59e0b' }} />
                        <Typography sx={{ fontSize: '18px', fontWeight: 600 }}>
                            {title}
                        </Typography>
                        <Iconify icon="mdi:information-outline" width={14} sx={{ color: '#94a3b8' }} />
                    </Box>
                </Tooltip>
                {total > 0 && (
                    <Chip
                        label={`${total} leads with a referral`}
                        size="small"
                        sx={{
                            bgcolor: '#fef3c7',
                            color: '#d97706',
                            fontWeight: 600,
                            fontSize: '12px',
                        }}
                    />
                )}
            </Box>

            {/* Referral List */}
            {referrals.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography color="text.secondary" sx={{ fontSize: '14px' }}>
                        No referral data available
                    </Typography>
                </Box>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {referrals.slice(0, 5).map((referral, index) => {
                        const config = getReferralConfig(referral.name);
                        const barWidth = (referral.leads / maxLeads) * 100;
                        const percentage = total > 0 ? (referral.leads / total) * 100 : 0;

                        return (
                            <Box
                                key={referral.name || index}
                                onClick={() => onReferralClick && onReferralClick(referral)}
                                sx={{
                                    cursor: onReferralClick ? 'pointer' : 'default',
                                    transition: 'all 0.2s ease',
                                    '&:hover': onReferralClick ? {
                                        transform: 'translateX(4px)',
                                    } : {},
                                }}
                            >
                                {/* Label Row */}
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        mb: 0.5,
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, maxWidth: '50%' }}>
                                        <Box
                                            sx={{
                                                width: '24px',
                                                height: '24px',
                                                borderRadius: '6px',
                                                bgcolor: config.bg,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                flexShrink: 0,
                                            }}
                                        >
                                            <Iconify
                                                icon={config.icon}
                                                width={14}
                                                height={14}
                                                sx={{ color: config.color }}
                                            />
                                        </Box>
                                        <Typography
                                            sx={{
                                                fontSize: '13px',
                                                fontWeight: 500,
                                                color: '#334155',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}
                                            title={referral.name}
                                        >
                                            {formatReferralLabel(referral.name)}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Typography
                                            sx={{
                                                fontSize: '13px',
                                                fontWeight: 600,
                                                color: '#1e293b',
                                            }}
                                        >
                                            {referral.leads} leads
                                        </Typography>
                                        <Tooltip title="Pipeline value: Total quoted value of leads created in this period" placement="top" arrow>
                                            <Typography
                                                sx={{
                                                    fontSize: '12px',
                                                    fontWeight: 600,
                                                    color: '#10b981',
                                                    cursor: 'help',
                                                }}
                                            >
                                                {formatCurrency(referral.revenue)}
                                            </Typography>
                                        </Tooltip>
                                    </Box>
                                </Box>

                                {/* Progress Bar */}
                                <Box
                                    sx={{
                                        height: '8px',
                                        bgcolor: '#fef3c7',
                                        borderRadius: '4px',
                                        overflow: 'hidden',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: `${barWidth}%`,
                                            height: '100%',
                                            background: 'linear-gradient(90deg, #f59e0b, #fbbf24)',
                                            borderRadius: '4px',
                                            transition: 'width 0.5s ease',
                                        }}
                                    />
                                </Box>

                                {/* Percentage */}
                                <Typography
                                    sx={{
                                        fontSize: '11px',
                                        color: '#94a3b8',
                                        mt: 0.25,
                                    }}
                                >
                                    {percentage.toFixed(1)}% of total
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>
            )}

            {/* View All Link */}
            {(showViewAll || referrals.length > 5) && (
                <Typography
                    onClick={handleViewAll}
                    sx={{
                        mt: 2,
                        pt: 2,
                        borderTop: '1px solid #e2e8f0',
                        fontSize: '13px',
                        color: '#3b82f6',
                        textAlign: 'center',
                        cursor: 'pointer',
                        fontWeight: 500,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 0.5,
                        '&:hover': {
                            textDecoration: 'underline',
                        },
                    }}
                >
                    View detailed referral report
                    <Iconify icon="eva:arrow-forward-fill" width={16} height={16} />
                </Typography>
            )}
        </Box>
    );
};

export default ReferralBreakdown;

ReferralBreakdown.propTypes = {
    referrals: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string,
            leads: PropTypes.number,
            revenue: PropTypes.number,
        })
    ),
    title: PropTypes.string,
    total: PropTypes.number,
    onReferralClick: PropTypes.func,
    showViewAll: PropTypes.bool,
};

ReferralBreakdown.defaultProps = {
    referrals: [],
    title: 'Top Referral Sources',
    total: 0,
    onReferralClick: null,
    showViewAll: true,
};
